/** @format */
import { Transition } from '@uirouter/core';
import { OPEN_UNIVERSITY_FEATURES_ENABLED_FOR_STUDENT_DEFAULT } from 'common-typescript/constants';
import {
    CourseUnit,
    OpenUniversityCart,
    OpenUniversityProduct,
    SuomiFiUserBasicDetails,
    UniversitySettings,
} from 'common-typescript/types';
import { firstValueFrom } from 'rxjs';
import {
    ConfirmDialogService,
    CourseUnitEntityService,
    OpenUniversityCartCustomerService,
    OpenUniversityProductEntityService,
    resolveTransitionParameter,
    SisFocusModeParams,
    SisStateDeclaration,
    SuomiFiService,
    UniversityService,
} from 'sis-components';
import { CartErrorComponent } from './cart/cart-error/cart-error.component';
import { CeeposPaymentCompleteComponent } from './cart/ceepos-payment-complete/ceepos-payment-complete.component';
import { MonetaryPaymentConfirmationComponent } from './cart/confirmation/monetary-payment-confirmation/monetary-payment-confirmation.component';
import { StudyVoucherPaymentConfirmationComponent } from './cart/confirmation/study-voucher-payment-confirmation/study-voucher-payment-confirmation.component';
import { OpenUniversityCartComponent } from './cart/open-university-cart.component';
import { MonetaryPaymentFailedComponent } from './cart/payment-failed/monetary-payment-failed/monetary-payment-failed.component';
import { StudyVoucherPaymentFailedComponent } from './cart/payment-failed/study-voucher-payment-failed/study-voucher-payment-failed.component';
import { PaymentProcessingComponent } from './cart/payment-processing/payment-processing.component';
import { PaytrailPaymentCompleteComponent } from './cart/paytrail-payment-complete/paytrail-payment-complete.component';
import { StudyVoucherConfirmationCompletedComponent } from './cart/study-voucher-confirmation-completed/study-voucher-confirmation-completed.component';
import { StudyVoucherStudiesActivateComponent } from './cart/study-voucher-studies-activate/study-voucher-studies-activate.component';
import { OpenUniversityEnrolmentWizardComponent } from './open-university-enrolment-wizard/open-university-enrolment-wizard.component';
import { OpenUniversityStudiesActivateWizardComponent } from './open-university-studies-activate-wizard/open-university-studies-activate-wizard.component';
import { SuomiFiErrorComponent } from './suomi-fi-error/suomi-fi-error.component';

const REDIRECT_STATE = 'student.course-unit.open-university';

const REDIRECT_LOGIN_STATE = 'student.login';

const isProductFoundInsideTheCart = (openUniCart: OpenUniversityCart, openUniProduct: OpenUniversityProduct, cu: CourseUnit) =>
    openUniCart?.items.some(item => item.openUniversityProductId === openUniProduct.id && openUniProduct.courseUnitId === cu.id);

const redirectWhenOpenUniProductMissingFromOpenUniCart = (trans: Transition) => {
    const openUniCartPromise = trans.injector().getAsync('openUniversityCart');
    const openUniProductPromise = trans.injector().getAsync('openUniversityProduct');
    const courseUnitPromise = trans.injector().getAsync('courseUnit');
    return Promise.all([openUniCartPromise, openUniProductPromise, courseUnitPromise])
        .then((data: [OpenUniversityCart, OpenUniversityProduct, CourseUnit]): undefined => {
            if (isProductFoundInsideTheCart(data[0], data[1], data[2])) {
                // All is good, do not redirect
                return undefined;
            }
            throw new Error(
                `Open-university-cart: ${data[0].id} does not contain open-university-product: ${data[1].id} with selected course-unit: ${data[2].id}`,
            );
        })
        .catch(err => {
            console.error(err.message);
            return { state: REDIRECT_STATE };
        });
};

const redirectWhenSuomiFiSessionMissing = async (trans: Transition) => {
    const suomiFiSessionPromise = trans.injector().getAsync('suomiFiUserBasicDetails');
    // If there is a error creating the suomi.fi session then prevent accessing the state.
    return suomiFiSessionPromise
        .then((): undefined => undefined)
        .catch((err: any) => {
            console.error(err.message);
            return { state: 'student.open-university-studies-activate' };
        });
};

const cartFocusMode: SisFocusModeParams = {
    titleKey: 'OPEN_UNIVERSITY_CART.CART',
    defaultExitState: 'student.search.open-university',
};

export const redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt =
    (state: string, altFunction?: Function) => ($transition$: Transition) =>
        firstValueFrom($transition$.injector().get(UniversityService).getCurrentUniversitySettings())
            .then((settings: UniversitySettings) => {
                const openUniversityFeaturesEnabledForStudent =
                    settings?.frontendFeatureToggles?.openUniversityFeaturesEnabledForStudent ??
                    OPEN_UNIVERSITY_FEATURES_ENABLED_FOR_STUDENT_DEFAULT;
                if (openUniversityFeaturesEnabledForStudent) {
                    return altFunction ? altFunction($transition$) : undefined; // undefined means no redirect
                }
                return { state };
            })
            .catch((err: any) => {
                if (err?.message) {
                    console.error(err.message);
                }
                return { state };
            });

export const openUniversityStates: SisStateDeclaration[] = [
    {
        name: 'student.course-unit.open-university-enrolment-wizard',
        url: '/open-uni-cart/:openUniversityCartId/open-uni-product/:openUniversityProductId/open-university-enrolment-wizard',
        focusMode: {
            defaultExitState: REDIRECT_STATE,
            titleKey: 'OPEN_UNIVERSITY.ENROLMENT_WIZARD.SELECT_TEACHING',
        },
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
        },
        views: {
            '@': { component: OpenUniversityEnrolmentWizardComponent }, // '@' overrides parent <ui-view>, which in this case contains the course unit info brochure
        },
        resolve: {
            courseUnit: courseUnitResolver,
            openUniversityCart: openUniversityCartResolver,
            openUniversityProduct: openUniversityProductResolver,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt(
            'student.course-unit.brochure',
            redirectWhenOpenUniProductMissingFromOpenUniCart,
        ),
        onExit: onOpenUniversityEnrolmentWizardExit,
    },
    {
        name: 'student.open-university-cart',
        // DON'T CHANGE THIS URL (unless you know what you're doing). Users will return to children states from external payment systems.
        url: '/open-university-cart',
        focusMode: cartFocusMode,
        redirectTo: 'student.open-university-cart.current-cart',
    },
    {
        name: 'student.open-university-cart.current-cart',
        url: '/current',
        component: OpenUniversityCartComponent,
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
    },
    {
        // DON'T CHANGE THIS URL (unless you know what you're doing). Backend redirect the zero payments here.
        name: 'student.open-university-cart.payment-successful',
        url: '/payment-successful?paymentId',
        component: MonetaryPaymentConfirmationComponent,
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
        },
        resolve: {
            paymentId: resolveTransitionParameter('paymentId'),
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
    },
    {
        name: 'student.open-university-cart.payment-failed',
        url: '/payment-failed',
        component: MonetaryPaymentFailedComponent,
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
    },
    {
        name: 'student.open-university-cart.payment-processing',
        url: '/payment-processing',
        component: PaymentProcessingComponent,
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
    },
    {
        name: 'student.open-university-cart.error',
        url: '/cart-error?errorType',
        component: CartErrorComponent,
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
    },
    {
        name: 'student.open-university-cart.ceepos-return-address',
        url: '/ceepos-confirmation',
        component: CeeposPaymentCompleteComponent,
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
    },
    {
        name: 'student.open-university-cart.paytrail-return-success',
        url: '/paytrail-confirmation',
        component: PaytrailPaymentCompleteComponent,
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
    },
    {
        name: 'student.open-university-cart.paytrail-return-cancel',
        url: '/paytrail-cancel',
        component: PaytrailPaymentCompleteComponent,
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
    },
    {
        name: 'student.open-university-cart.study-voucher-return-success',
        // NOTE! This URL and the "transactionId" parameter are coupled with a frontend activation URL hardcoded in backend
        // (in StudyVoucherActivationUrlService) and should not be changed without changing the other one too.
        url: '/study-voucher-confirmation?transactionId',
        component: StudyVoucherPaymentConfirmationComponent,
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
        },
        resolve: {
            transactionId: resolveTransitionParameter('transactionId'),
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
    },
    {
        name: 'student.open-university-cart.study-voucher-return-failure',
        // NOTE! This URL is coupled with a frontend payment failure URL hardcoded in backend
        // (in StudyVoucherActivationUrlService) and should not be changed without changing the other one too.
        // Opin.fi delivers a transactionId parameter here too, which the view has no use for, but which is declared so that
        // it's recognized as a parameter of this state instead of an unknown one.
        url: '/study-voucher-payment-failure?transactionId',
        component: StudyVoucherPaymentFailedComponent,
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
    },
    {
        name: 'student.open-university-cart.study-voucher-studies-activate-suomi-fi',
        url: '/study-voucher/:transactionId/studies-activate-suomi-fi',
        component: StudyVoucherStudiesActivateComponent,
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
            browserTabTitle: 'OPEN_UNIVERSITY.STUDY_VOUCHER_STUDIES_ACTIVATE.PAGE_TAB_TITLE',
        },
        resolve: {
            transactionId: resolveTransitionParameter('transactionId'),
            suomiFiUserBasicDetails: suomiFiSessionResolver,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt(
            'student.logged-in.frontpage',
            redirectWhenSuomiFiSessionMissing,
        ),
    },
    {
        name: 'student.open-university-cart.study-voucher-confirmation-completed',
        url: '/study-voucher/confirmation-completed',
        component: StudyVoucherConfirmationCompletedComponent,
        params: {
            cartParam: null,
        },
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
        },
        resolve: {
            openUniversityCart: openUniversityCartFromParamsResolver,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
    },
    {
        name: 'student.open-university-cart.study-voucher-payment-failed',
        url: '/study-voucher/payment-failed',
        component: StudyVoucherPaymentFailedComponent,
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
    },
    {
        name: 'student.open-university-studies-activate',
        //  DON'T CHANGE THIS URL! /open-university-studies-activate route is referenced in open university emails, check
        //  MonetaryPaymentConfirmationEmailContentBuilder.ACTIVATION_LINK_TEMPLATE
        url: '/open-uni-cart/:openUniversityCartId/activation-code/:activationCode/open-university-studies-activate',
        focusMode: {
            defaultExitState: 'student.logged-in.frontpage',
            titleKey: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.TITLE',
        },
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
            browserTabTitle: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.STEP1.PAGE_TAB_TITLE',
        },
        views: {
            '': { component: OpenUniversityStudiesActivateWizardComponent },
        },
        resolve: {
            openUniversityCart: openUniversityCartWithActivationCodeResolver,
            currentStep: openUniversityStudiesActivateWizardStepResolver,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
        onExit: onOpenUniversityActivateWizardExit,
    },
    {
        name: 'student.logged-in.open-university-studies-activate',
        url: '/open-uni-cart/:openUniversityCartId/activation-code/:activationCode/open-university-studies-activate-authenticated',
        focusMode: {
            defaultExitState: 'student.logged-in.frontpage',
            titleKey: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.TITLE',
        },
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
            browserTabTitle: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.STEP1.PAGE_TAB_TITLE',
        },
        views: {
            '': { component: OpenUniversityStudiesActivateWizardComponent },
        },
        resolve: {
            openUniversityCart: openUniversityCartWithActivationCodeResolver,
            currentStep: openUniversityStudiesActivateWizardStepResolver,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
        onExit: onOpenUniversityActivateWizardExit,
    },
    {
        name: 'student.open-university-studies-activate-suomi-fi',
        url: '/open-uni-cart/:openUniversityCartId/activation-code/:activationCode/open-university-studies-activate-authenticated-suomi-fi',
        focusMode: {
            defaultExitState: REDIRECT_LOGIN_STATE,
            titleKey: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.TITLE',
        },
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
            browserTabTitle: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.STEP2.PAGE_TAB_TITLE',
        },
        views: {
            '': { component: OpenUniversityStudiesActivateWizardComponent },
        },
        resolve: {
            openUniversityCart: openUniversityCartWithActivationCodeResolver,
            suomiFiUserBasicDetails: suomiFiSessionResolver,
            currentStep: openUniversityStudiesActivateWizardStepResolver,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt(
            'student.logged-in.frontpage',
            redirectWhenSuomiFiSessionMissing,
        ),
        onExit: onOpenUniversityActivateWizardExit,
    },
    {
        name: 'student.logged-in.open-university-studies-activate-confirmation-completed',
        url: '/open-uni-cart/open-university-studies-activate/confirmation-completed',
        params: {
            cartParam: null,
            isStaffUserParam: false,
        },
        focusMode: {
            defaultExitState: 'student.logged-in.frontpage',
            titleKey: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.TITLE',
        },
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
            browserTabTitle: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.STEP3.PAGE_TAB_TITLE',
        },
        views: {
            '': { component: OpenUniversityStudiesActivateWizardComponent },
        },
        resolve: {
            openUniversityCart: openUniversityCartFromParamsResolver,
            currentStep: openUniversityStudiesActivateWizardStepResolver,
            isStaffUser: isStaffUserFromParamsResolver,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
    },
    {
        name: 'student.open-university-studies-activate-confirmation-completed-suomi-fi',
        url: '/open-uni-cart/open-university-studies-activate/confirmation-completed-suomi-fi',
        params: {
            cartParam: null,
            emailParam: null,
        },
        focusMode: {
            defaultExitState: REDIRECT_LOGIN_STATE,
            titleKey: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.TITLE',
        },
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
            browserTabTitle: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.STEP3.PAGE_TAB_TITLE',
        },
        views: {
            '': { component: OpenUniversityStudiesActivateWizardComponent },
        },
        resolve: {
            openUniversityCart: openUniversityCartFromParamsResolver,
            currentStep: openUniversityStudiesActivateWizardStepResolver,
            secondaryEmail: emailFromParamsResolver,
        },
        redirectTo: redirectToStateIfOpenUniversityFeaturesAreNotEnabledForStudentOrAlt('student.logged-in.frontpage'),
        onExit: onOpenUniversityActivateWizardExit,
    },
    {
        name: 'student.suomi-fi',
        url: '/suomi-fi',
        component: SuomiFiErrorComponent,
        data: {
            showHeader: false,
            headerWithoutBreadcrumbs: true,
        },
    },
];

export function onOpenUniversityEnrolmentWizardExit($transition$: Transition) {
    const confirmDialogService = $transition$.injector().get(ConfirmDialogService);
    if ($transition$.$to().name === $transition$.$from().name) {
        return;
    }
    if ($transition$.options().custom.skipConfirmationDialog) {
        return;
    }
    const texts = {
        title: 'OPEN_UNIVERSITY.ENROLMENT_WIZARD.DISMISS_MODAL_TITLE',
        descriptions: ['OPEN_UNIVERSITY.ENROLMENT_WIZARD.DISMISS_MODAL_DESC_1', 'OPEN_UNIVERSITY.ENROLMENT_WIZARD.DISMISS_MODAL_DESC_2'],
        confirmText: 'OPEN_UNIVERSITY.ENROLMENT_WIZARD.DISMISS_MODAL_BUTTON',
    };
    return confirmDialogService.confirm(texts);
}
onOpenUniversityEnrolmentWizardExit.$inject = ['$transition$'];

export function onOpenUniversityActivateWizardExit($transition$: Transition) {
    const confirmDialogService = $transition$.injector().get(ConfirmDialogService);
    const suomiFiService = $transition$.injector().get(SuomiFiService);
    if ($transition$.$to().name === $transition$.$from().name) {
        return;
    }
    if ($transition$.options().custom.skipConfirmationDialog) {
        return;
    }

    if ($transition$.$from().name.includes('studies-activate-suomi-fi')) {
        const suomiFitexts = {
            title: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.DISMISS_MODAL_TITLE',
            descriptions: [
                'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.DISMISS_MODAL_DESC_SUOMI_FI',
                'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.DISMISS_MODAL_DESC2',
            ],
            confirmText: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.DISMISS_MODAL_BUTTON',
        };
        return confirmDialogService.confirm(suomiFitexts).then(() => suomiFiService.logout($transition$.$to().url.pattern));
    }

    if ($transition$.$from().name.includes('open-university-studies-activate-confirmation-completed')) {
        const confirmTexts = {
            title: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.DISMISS_MODAL_TITLE',
            descriptions: [
                'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.DISMISS_MODAL_DESC_SUOMI_FI_STEP_3',
                'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.DISMISS_MODAL_DESC2',
            ],
            confirmText: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.DISMISS_MODAL_BUTTON',
        };
        return confirmDialogService.confirm(confirmTexts);
    }

    const defaultTexts = {
        title: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.DISMISS_MODAL_TITLE',
        descriptions: [
            'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.DISMISS_MODAL_DESC_DEFAULT',
            'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.DISMISS_MODAL_DESC2',
        ],
        confirmText: 'OPEN_UNIVERSITY.STUDIES_ACTIVATE_WIZARD.DISMISS_MODAL_BUTTON',
    };
    return confirmDialogService.confirm(defaultTexts);
}
onOpenUniversityActivateWizardExit.$inject = ['$transition$'];

export function courseUnitResolver($transition$: Transition): Promise<CourseUnit> {
    return firstValueFrom($transition$.injector().get(CourseUnitEntityService).get($transition$.params().courseUnitId));
}
courseUnitResolver.$inject = ['$transition$'];

export function openUniversityCartResolver($transition$: Transition): Promise<OpenUniversityCart> {
    return firstValueFrom($transition$.injector().get(OpenUniversityCartCustomerService).getCurrentCart());
}
openUniversityCartResolver.$inject = ['$transition$'];

export function openUniversityProductResolver($transition$: Transition): Promise<OpenUniversityProduct[]> {
    return firstValueFrom(
        $transition$
            .injector()
            .get<OpenUniversityProductEntityService>(OpenUniversityProductEntityService)
            .get($transition$.params().openUniversityProductId),
    );
}
openUniversityProductResolver.$inject = ['$transition$'];

export function openUniversityCartWithActivationCodeResolver($transition$: Transition): Promise<OpenUniversityCart> {
    return firstValueFrom(
        $transition$
            .injector()
            .get(OpenUniversityCartCustomerService)
            .getWithActivationCode($transition$.params().openUniversityCartId, $transition$.params().activationCode),
    );
}
openUniversityCartWithActivationCodeResolver.$inject = ['$transition$'];

export function openUniversityCartFromParamsResolver($transition$: Transition): Promise<OpenUniversityCart> {
    return new Promise(resolve => {
        resolve($transition$.params().cartParam);
    });
}
openUniversityCartFromParamsResolver.$inject = ['$transition$'];

export function emailFromParamsResolver($transition$: Transition): string {
    return $transition$.params().emailParam;
}
emailFromParamsResolver.$inject = ['$transition$'];

export function isStaffUserFromParamsResolver($transition$: Transition): boolean {
    return $transition$.params().isStaffUserParam;
}
isStaffUserFromParamsResolver.$inject = ['$transition$'];

export function suomiFiSessionResolver($transition$: Transition): Promise<SuomiFiUserBasicDetails> {
    return firstValueFrom($transition$.injector().get(SuomiFiService).login());
}
suomiFiSessionResolver.$inject = ['$transition$'];

export function openUniversityStudiesActivateWizardStepResolver($transition$: Transition): number {
    const to = $transition$.$to().name;
    if (to.includes('open-university-studies-activate-confirmation-completed')) {
        return 2;
    }
    if (to.includes('student.logged-in.') || to.includes('suomi-fi')) {
        return 1;
    }
    return 0;
}
openUniversityStudiesActivateWizardStepResolver.$inject = ['$transition$'];
